import { Footer } from './components/landing/marketing/Footer';
import { LegalOutboxHost } from './theaWeb/legal/LegalOutboxHost';
import { HubNavigationFrame } from './theaWeb/hub/HubNavigationFrame';
import React, { lazy, Suspense } from "react";
import { Navigate, Routes, Route, useNavigate } from "react-router-dom";
import { ThemeProvider } from "styled-components";

import { LandingPage } from "./components/landing/marketing/LandingPage";
import { MomsLandingPage } from "./components/landing/marketing/MomsLandingPage";
import { RouteShellFallback } from "./components/landing/RouteShellFallback";
import { AuthGateProvider, useAuthGate } from "./theaWeb/auth/AuthGateContext";
import { AuthConfirmationProvider } from "./theaWeb/auth/authConfirmation";
import { MergeStateProvider } from "./theaWeb/auth/MergeStateContext";
import { RegenerateProgressProvider } from "./theaWeb/board/RegenerateProgressContext";
import { consumeBecomeTokenOnce } from "./theaWeb/auth/becomeUser";
import { ImpersonationBanner } from "./theaWeb/components/ImpersonationBanner";
import { FirebaseProvider, useAuth } from "./theaWeb/firebase/FirebaseContext";
import { TrebuchetProvider } from "./theaWeb/trebuchet/TrebuchetContext";
import { useGaUserIdentity } from "./theaWeb/hooks/useGaUserIdentity";
import { useSyncTimezone } from "./theaWeb/hooks/useSyncTimezone";
import { useSovrnOverlay } from "./theaWeb/hooks/useSovrnOverlay";
import { useScrollRestoration } from "./theaWeb/hooks/useScrollRestoration";
import { PageTrackingMount } from "./theaWeb/components/PageTrackingMount";
import { RouteFocusMount } from "./theaWeb/components/RouteFocusMount";
import { PrerenderedLazyRoute } from "./prerender/PrerenderedLazyRoute";
import { ConsentBannerHost } from "./theaWeb/consent/ConsentBannerHost";
import { OccasionRedirect } from "./theaWeb/hub/occasionRedirect";
import { gaFirstRender } from "./theaWeb/lib/gaPixel";
import { theme } from "./theme";

function LandingRoute() {
  const navigate = useNavigate();
  const { requestSignIn } = useAuthGate();

  return (
    <LandingPage
      // entry_point is provided by LandingPage's hero sub-components
      // so the dashboard can split funnel completion rates per surface.
      onCtaClick={(entry_point) =>
        navigate("/quiz", { state: { from: "/", entry_point } })
      }
      // entry is the CTA's data-signin-entry value — carried onto the GA4
      // sign_in_opened event, same per-surface split as entry_point above.
      onSignInClick={(entry) => requestSignIn({ mode: "signin", entry })}
    />
  );
}

// /welcome2 — the working-moms campaign homepage variant. Same shell as
// LandingRoute; MomsLandingPage supplies the mom-targeted hero/beats/banner
// and remaps entry points to the moms_* vocabulary. `from: "/welcome2"` keeps
// quiz back-nav returning to the variant the visitor landed on.
function MomsLandingRoute() {
  const navigate = useNavigate();
  const { requestSignIn } = useAuthGate();

  return (
    <MomsLandingPage
      onCtaClick={(entry_point) =>
        navigate("/quiz", { state: { from: "/welcome2", entry_point } })
      }
      onSignInClick={(entry) => requestSignIn({ mode: "signin", entry })}
    />
  );
}

// Dev surfaces are lazy-loaded so the customer-facing root bundle stays slim.
// Lazy is not private: anyone who types a /dev/* URL on givethea.com gets the
// chunk. The legacy /dev/login + /dev/signup pages were removed for exactly
// that reason — they created real accounts outside the v4 sign-in doors.
const CarouselFeedPage = lazy(() => import("./components/carousel/CarouselFeedPage"));
const QuizPage = lazy(() => import("./theaWeb/pages/QuizPage"));
const RecommendationResultsPage = lazy(() =>
  import("./theaWeb/pages/RecommendationResultsPage"),
);
const BoardRoute = lazy(() => import("./theaWeb/pages/BoardRoute"));
// MyPeoplePage retired 2026-05-13: the homepage's "Your People" section
// is the single surface (avatar dropdown link anchor-scrolls there).
// See research write-up in Kate's QA thread — Spotify/Airbnb/Pinterest
// all use a single surface for "your collection" rather than duplicating
// across homepage + dedicated page.
// Named chunks, not webpack's numeric default. /terms and /privacy are the
// only two routes that ship a prerendered body AND load their component from a
// lazy chunk, so the prerenderer has to preload that exact chunk in their HTML
// — otherwise the visitor reads the prerendered policy, watches React replace
// it with the Suspense shimmer while the chunk downloads, and then gets the
// policy back. A stable name is how the build step finds the chunk in
// asset-manifest.json; a numeric id would change with unrelated edits. See
// scripts/og/bodyRoutes.js (PRELOAD_CHUNKS) and the guard in prerender/bodies.mjs.
const TermsPage = lazy(() =>
  import(/* webpackChunkName: "legal-terms" */ "./theaWeb/pages/TermsPage"),
);
const PrivacyPage = lazy(() =>
  import(/* webpackChunkName: "legal-privacy" */ "./theaWeb/pages/PrivacyPage"),
);
const SharedBoardPage = lazy(() => import("./theaWeb/pages/SharedBoardPage"));
const CalendarPage = lazy(() => import("./theaWeb/calendar/CalendarPage"));
const SettingsPage = lazy(() => import("./theaWeb/pages/SettingsPage"));
const HolidaysPage = lazy(() => import("./theaWeb/pages/HolidaysPage"));
const YouPage = lazy(() => import("./theaWeb/pages/YouPage"));
const GiftGuidesPage = lazy(() => import("./theaWeb/pages/GiftGuidesPage"));
const LikedPage = lazy(() => import("./theaWeb/pages/LikedPage"));
const AllSavedPage = lazy(() => import("./theaWeb/pages/AllSavedPage"));
const LikedThumbnailCompare = lazy(() => import("./theaWeb/dev/LikedThumbnailCompare"));
/* /v4 — design-reference gallery + tap-through walkthrough for the rebuilt
   sign-in / email-capture flow. Lazy like every other non-root surface; renders
   only prop-driven, Firebase-free components against a synthetic account state.
   The REAL v4 flow (flagged surfaces + /claim) is wired separately. */
const V4PreviewPage = lazy(() => import("./theaWeb/v4/V4PreviewPage"));
const V4JourneyPage = lazy(() => import("./theaWeb/v4/V4JourneyPage"));
/* /claim?g=<token> — the REAL guest→account claim page (the emailed link lands
   here). getClaimPreview → board preview → provider auth → claimBoard (#152).
   Not flagged: it is reached only by an email link, and is inert without a token. */
const V4ClaimRoute = lazy(() => import("./theaWeb/v4/V4ClaimRoute"));
/* Bridge: the SINGLE owner (AuthGateProvider) publishes the mobile-OAuth
   redirect outcome; inject it into V4ClaimRoute as a prop so the /claim route
   stays decoupled from AuthGateContext (the v4 account-door fitness invariant
   keeps v4/ free of the sign-in-door context). */
function V4ClaimRouteWithOutcome() {
  const { redirectOutcome } = useAuthGate();
  return <V4ClaimRoute redirectOutcome={redirectOutcome} />;
}
const GuidePage = lazy(() => import("./theaWeb/guide/GuidePage"));
// Private render surface for the admin app's guide preview iframe (postMessage
// handshake pinned to the admin origin; nothing read from Firestore). See
// GuidePreviewPage.tsx and thea/utils/guide_publication.py.
const GuidePreviewPage = lazy(() => import("./theaWeb/guide/GuidePreviewPage"));

// /occasion/* is RETIRED (THE-587). The route now redirects to the single
// occasion URL family, /gift-guides/{topic}; the marketing pages behind it
// rendered hardcoded sample arrays, not live catalogue, and dropping the only
// route that imported them takes that chunk out of the build.
//
// The topic-hub template serves BOTH /gift-guides/:topic and
// /gift-guides/search?q= — one component, because the plan requires a curated
// topic and a typed query to render the identical screen.
const TopicHubPage = lazy(() => import("./theaWeb/hub/TopicHubPage"));

// PageTrackingMount (the Meta pixel PageView mount) deliberately lives in
// `./theaWeb/components/PageTrackingMount`, NOT here. Its two hooks are
// declaration-order-sensitive — that order is THE-787's entire `dl` fix — and
// the suite that pins it has to render the shipping component, which it cannot
// do if the component is buried in App.js's import graph. Read the hazard note
// there before touching it, and do not re-inline it here:
// `src/__tests__/pageTrackingMountIsShared.test.ts` fails the build if App.js
// declares `usePageTracking` or `useCalendarDeepLinkCapture` itself.

// Fires `page_first_render` exactly once per app load. Mounted as a sibling
// to PageTrackingMount so it runs after the first route's first commit.
// Pairs with bounce-attribution analysis: a Meta-reported LPV without a
// matching gaFirstRender means the React app failed to mount (bug or
// webview crash), distinct from "rendered but user bounced fast."
function FirstRenderMount() {
  React.useEffect(() => {
    gaFirstRender();
  }, []);
  return null;
}

// Wires GA4 `user_id` to the Firebase UID and fires the GA4 `sign_up` event
// on the (anon|null) → permanent transition. Mounted inside FirebaseProvider
// because the hook calls useAuth(). One mount per app load is enough.
function GaUserIdentityMount() {
  useGaUserIdentity();
  return null;
}

// Consumes a boot-time ?becomeToken= (admin impersonation — becomeUser.ts) and
// installs the impersonator-claim watcher. ensureAuth() awaits the same
// single-flight promise; this mount covers routes where nothing calls it.
function BecomeUserMount() {
  const auth = useAuth();
  React.useEffect(() => {
    void consumeBecomeTokenOnce(auth);
  }, [auth]);
  return null;
}

// Reports the device timezone to the BE (theaWebSyncTimezone) so the day-of
// birthday email can land on the user's LOCAL morning. Guarded per
// (uid, tz) in localStorage — one callable fire per change, silent otherwise.
function TimezoneSyncMount() {
  useSyncTimezone();
  return null;
}

// Subscribes to the Sovrn click-out overlay doc so the daily canary's
// automatic unwrap/re-wrap of broken affiliate merchants reaches users
// without a deploy. Inside FirebaseProvider (the hook calls useDb()).
function SovrnOverlayMount() {
  useSovrnOverlay();
  return null;
}

// App-level scroll restoration: fresh navigations land at the top; back /
// forward restores where the user was. Fixes entering a board fresh from My
// People landing half-scrolled. See SCROLL_RESTORATION_PLAN.md.
function ScrollRestorationMount() {
  useScrollRestoration();
  return null;
}

function App() {
  const navigate = useNavigate();
  // One shared Suspense fallback for every lazy route: real SiteHeader +
  // shimmer content skeleton, so a slow chunk shows brand chrome instead
  // of a blank white page (header/IA spec v5 PR 2). Also on /dev routes —
  // uniformity keeps the no-blank-fallback fitness test simple
  // (src/__tests__/noNullRouteFallbacks.test.ts).
  const routeShell = <RouteShellFallback />;
  // Loss-cohort landing: a guest who signs into an account they already have is
  // sent to their own boards (the People/home grid), mirroring V4ClaimRoute's
  // existing-account exit (navigate('/', { replace: true })) — not left stranded
  // on the orphaned guest-board URL. AuthConfirmationProvider fires this on the
  // `welcome_back` outcome.
  const goHomeToBoards = () => navigate("/", { replace: true });
  return (
    <ThemeProvider theme={theme}>
      <FirebaseProvider>
        {/* Trebuchet selective-launch flags: one server-evaluated fetch per
            auth state, surfaced through useTrebuchet. Inside FirebaseProvider
            (it awaits ensureAuth and watches the live uid). */}
        <TrebuchetProvider>
        <MergeStateProvider>
            <AuthConfirmationProvider onWelcomeBackNavigate={goHomeToBoards}>
            <AuthGateProvider>
              <LegalOutboxHost />
            <RegenerateProgressProvider>
        <BecomeUserMount />
        <PageTrackingMount />
        <FirstRenderMount />
        <GaUserIdentityMount />
        <TimezoneSyncMount />
        <ScrollRestorationMount />
        <SovrnOverlayMount />
        {/* A11y: announce SPA route changes + move focus to the new page's
            <main> (row 122). Renders the app's single polite live region. */}
        <RouteFocusMount />
        {/* Cookie/tracking consent banner. Global — every visitor is asked,
            and no client-side tag fires until they accept. Renders nothing
            until the browser is idle, so it is off the critical render path;
            see ConsentBannerHost. */}
        <ConsentBannerHost />
        {/* Admin impersonation strip (?becomeToken= sessions). Renders null
            for everyone else; keyed on the impersonator ID-token claim, so it
            survives reloads for the life of the session. */}
        <ImpersonationBanner />
        <HubNavigationFrame>
        <Routes>
          <Route path="/" element={<LandingRoute />} />
          <Route path="/welcome2" element={<MomsLandingRoute />} />
          {/* RETIRED (THE-587). Redirects, does not 404: the calendar's
              Mother's Day / Father's Day / Teacher Appreciation cards point
              here, and the URLs have been live long enough to be bookmarked.
              The query string rides along so a preview-cohort tester is not
              ejected from ?giftGuideHub=1 by following a legacy link. */}
          <Route path="/occasion/:id" element={<OccasionRedirect />} />
          <Route
            path="/dev/feed"
            element={
              <Suspense fallback={routeShell}>
                <CarouselFeedPage />
              </Suspense>
            }
          />
          <Route
            path="/dev/liked-compare"
            element={
              <Suspense fallback={routeShell}>
                <LikedThumbnailCompare />
              </Suspense>
            }
          />
          <Route
            path="/quiz"
            element={
              <Suspense fallback={routeShell}>
                <QuizPage />
              </Suspense>
            }
          />
          <Route
            path="/quiz/results/:recipientId/:recommendationId"
            element={
              <Suspense fallback={routeShell}>
                <RecommendationResultsPage />
              </Suspense>
            }
          />
          <Route
            path="/terms"
            element={
              /* Prerendered body (THE-678): renders the component synchronously
                 when index.js preloaded it for this landing, so React's first
                 commit does not flash the route shimmer over legal text that is
                 already on screen. Falls back to Suspense on every other path
                 in, which is what an in-app navigation here takes. */
              <PrerenderedLazyRoute path="/terms" fallback={routeShell}>
                <TermsPage />
              </PrerenderedLazyRoute>
            }
          />
          <Route
            path="/privacy"
            element={
              /* Prerendered body (THE-678): renders the component synchronously
                 when index.js preloaded it for this landing, so React's first
                 commit does not flash the route shimmer over legal text that is
                 already on screen. Falls back to Suspense on every other path
                 in, which is what an in-app navigation here takes. */
              <PrerenderedLazyRoute path="/privacy" fallback={routeShell}>
                <PrivacyPage />
              </PrerenderedLazyRoute>
            }
          />
          <Route
            path="/guide/:slug"
            element={
              <Suspense fallback={routeShell}>
                <GuidePage />
              </Suspense>
            }
          />
          <Route
            path="/guide-preview"
            element={
              <Suspense fallback={routeShell}>
                <GuidePreviewPage />
              </Suspense>
            }
          />
          <Route
            path="/board/:recipientId"
            element={
              <Suspense fallback={routeShell}>
                <BoardRoute />
              </Suspense>
            }
          />
          <Route
            path="/b/:shareId"
            element={
              <Suspense fallback={routeShell}>
                <SharedBoardPage />
              </Suspense>
            }
          />
          <Route
            path="/liked"
            element={
              <Suspense fallback={routeShell}>
                <AllSavedPage />
              </Suspense>
            }
          />
          <Route
            path="/liked/:recipientId"
            element={
              <Suspense fallback={routeShell}>
                <LikedPage />
              </Suspense>
            }
          />
          <Route
            path="/calendar"
            element={
              <Suspense fallback={routeShell}>
                <CalendarPage />
              </Suspense>
            }
          />
          <Route
            path="/settings"
            element={<Suspense fallback={routeShell}><SettingsPage /></Suspense>}
          />
          <Route
            path="/holidays"
            element={<Suspense fallback={routeShell}><HolidaysPage /></Suspense>}
          />
          <Route
            path="/you"
            element={
              <Suspense fallback={routeShell}>
                <YouPage />
              </Suspense>
            }
          />
          <Route
            path="/gift-guides"
            element={
              <Suspense fallback={routeShell}>
                <GiftGuidesPage />
              </Suspense>
            }
          />
          {/* Slice B (THE-587). `search` is a RESERVED slug under the same
              prefix, matched by THIS pattern — there is deliberately no
              sibling static Route for /gift-guides/search, and adding one back
              silently breaks the search route.

              React Router v6 ranks routes by specificity, not by declaration
              order, so a static `/gift-guides/search` outranks `:topic` and
              wins — and inside it `params.topic` is UNDEFINED. TopicHubPage
              derives `isSearchRoute` from that param, so the static route made
              every `/gift-guides/search?q=...` read as a topic page with an
              empty slug: `q` was never read at all. One pattern is also what
              makes "a typed query renders the identical template" structural
              rather than two components agreeing to look alike. Guarded by a
              source-level test in topicHubDeadEnd.test.tsx. */}
          <Route
            path="/gift-guides/:topic"
            element={
              <Suspense fallback={routeShell}>
                <TopicHubPage />
              </Suspense>
            }
          />
          <Route
            path="/v4"
            element={
              <Suspense fallback={routeShell}>
                <V4PreviewPage />
              </Suspense>
            }
          />
          <Route
            path="/v4/journey"
            element={
              <Suspense fallback={routeShell}>
                <V4JourneyPage />
              </Suspense>
            }
          />
          <Route
            path="/claim"
            element={
              <Suspense fallback={routeShell}>
                <V4ClaimRouteWithOutcome />
              </Suspense>
            }
          />
          {/* /people route retired — see comment above the lazy import.
              Avatar dropdown link now anchors to /#your-people on the
              homepage. */}
          <Route path="/people" element={<Navigate to="/#your-people" replace />} />
        </Routes>
        <Footer />
        </HubNavigationFrame>
            </RegenerateProgressProvider>
            </AuthGateProvider>
            </AuthConfirmationProvider>
        </MergeStateProvider>
        </TrebuchetProvider>
      </FirebaseProvider>
    </ThemeProvider>
  );
}

export default App;
